import {
  SVT_CORNER,
  SVT_FIELD,
  SVT_FIELD_INVALID,
} from '@eliadmin/core/src/components/ui/svetech-shape';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import type { VariantProps } from 'class-variance-authority';
import { cva } from 'class-variance-authority';
import * as React from 'react';

/*
 * SveTech field (Key design „Pole“): white box with a 1 px border that turns green on focus (no
 * ring) and red when invalid, 56 → 50 px, radius 12 → 15 px with the square top-right corner.
 * `filled` (core default) and `outlined` look the same. Same API, slots and `ea-*` classes as the core.
 */
const inputVariants = cva(
  cn(
    'border-box relative flex h-[var(--svt-field-h)] w-full items-center gap-2',
    'svt-body focus-within:border-[#2c7a25] has-[>input:disabled]:cursor-not-allowed has-[>input:disabled]:opacity-70',
    SVT_FIELD,
  ),
  {
    variants: {
      variant: {
        outlined: ['ea-input__outlined'],
        filled: ['ea-input__filled'],
      },

      rounded: {
        default: [SVT_CORNER, 'ea-input__wrapper--rounded-default'],
        none: ['rounded-none', 'ea-input__wrapper--rounded-none'],
      },
    },

    defaultVariants: {
      rounded: 'default',
      variant: 'filled',
    },
  },
);
export interface InputProps
  extends
    Omit<React.InputHTMLAttributes<HTMLInputElement>, 'className'>,
    VariantProps<typeof inputVariants> {
  before?: React.ReactNode;
  after?: React.ReactNode;

  beforeOuter?: React.ReactNode;
  afterOuter?: React.ReactNode;

  className?: {
    wrapper?: string;
    main?: string;
    before?: string;
    after?: string;
  };
  classNames?: {
    wrapper?: string;
    main?: string;
    before?: string;
    after?: string;
  };
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ className, type, before, after, afterOuter, beforeOuter, classNames, ...props }, ref) => {
    return (
      <>
        {beforeOuter}
        <div
          className={cn(
            inputVariants({
              rounded: props.rounded,
              variant: props.variant,
            }),
            'ea-input__wrapper',
            props['aria-invalid'] && ['ea-input__wrapper--invalid', SVT_FIELD_INVALID],
            className?.wrapper,
            classNames?.wrapper,
          )}
        >
          <div
            className={cn(
              'absolute inset-y-0 left-0 flex h-full items-center pl-1',
              className?.before,
              classNames?.before,
            )}
          >
            {before}
          </div>
          <input
            type={type}
            className={cn(
              'svt-body h-full w-full flex-1 overflow-hidden rounded-[inherit] bg-transparent px-[var(--svt-field-px)] py-1',
              'placeholder:text-[#6b7681] hover:outline-none focus:outline-none disabled:cursor-not-allowed',
              'file:h-full file:cursor-pointer file:rounded-md file:border-none file:bg-[#f2f3ef] file:outline-none file:hover:bg-[#e6e8e2]',
              !!before && 'pl-9',
              !!after && 'pr-9',
              'ea-input__main',
              className?.main,
              classNames?.main,
            )}
            ref={ref}
            {...props}
          />
          <div
            className={cn(
              'absolute inset-y-0 right-0 flex h-full items-center pr-1',
              className?.after,
              classNames?.after,
            )}
          >
            {after}
          </div>
        </div>
        {afterOuter}
      </>
    );
  },
);
Input.displayName = 'Input';

export { Input };
